import { collection, onSnapshot, query, where, Timestamp, type Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase';

// Forma del documento de `vehicles-live` — docs/Paquete_Arranque_Frontend.md
// §6.1. Es una proyección de "ahora", no la verdad (ADR-008): solo trae lo
// que cambia con el movimiento, nunca plate/model/driver (eso sigue
// viniendo de GET /vehicles).
export interface VehicleLiveDoc {
  vehicleId: string;
  subunitId?: string;
  coords?: { lat: number; lng: number };
  speedKmh?: number;
  state: string;
  updatedAt?: string;
  /**
   * Última señal REAL del vehículo (su última posición o latido). Para
   * «visto hace X»: NO es `updatedAt`, que solo dice cuándo escribimos el
   * documento (bugfix #59, Paquete §6.1). Puede venir ausente o nulo: el
   * equipo nunca reportó.
   */
  lastSeenAt?: string;
}

// Hallazgo verificando FE-03 contra datos reales: el ejemplo JSON del
// contrato muestra `updatedAt` como string ISO, pero el documento real de
// Firestore lo guarda como `Timestamp` nativo (el core lo escribe con el
// Admin SDK). Se normaliza aquí, en el único lugar que lee el doc crudo, así
// el resto del front sigue trabajando con ISO strings como en todas partes.
function normalizeLiveDoc(raw: Record<string, unknown>): VehicleLiveDoc {
  const updatedAt = raw.updatedAt instanceof Timestamp ? raw.updatedAt.toDate().toISOString() : undefined;
  // lastSeenAt llega como Timestamp nativo, igual que updatedAt (el core lo
  // escribe con el Admin SDK). Puede faltar: el equipo nunca reportó.
  const lastSeenAt = raw.lastSeenAt instanceof Timestamp ? raw.lastSeenAt.toDate().toISOString() : undefined;
  return { ...raw, updatedAt, lastSeenAt } as VehicleLiveDoc;
}

/**
 * Se suscribe a la colección de vehículos en vivo de un tenant, aplicando el
 * filtro por sub-unidad cuando aplica. Contrato §6.1: una regla de Firestore
 * que niega UN documento hace fallar la consulta ENTERA — por eso el filtro
 * `where('subunitId','in', subunitIds)` es obligatorio para roles no
 * consolidados, y se omite del todo (colección completa) cuando `subunitIds`
 * viene vacía (Administrador/Ejecutivo).
 */
export function subscribeVehiclesLive(
  collectionPath: string,
  subunitIds: string[],
  onUpdate: (docs: VehicleLiveDoc[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const coll = collection(db, collectionPath);
  const target = subunitIds.length === 0 ? coll : query(coll, where('subunitId', 'in', subunitIds));

  return onSnapshot(
    target,
    snapshot => onUpdate(snapshot.docs.map(doc => normalizeLiveDoc(doc.data()))),
    error => onError(error),
  );
}
